import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Collapse } from "antd";
import { Viewer, Worker } from "@react-pdf-viewer/core";
import playbutton from "../../assets/images/playbutton.svg";
import paycheck1 from "../../assets/images/paycheck1.svg";
import noicon from "../../assets/images/noicon.svg";
import yesicon from "../../assets/images/yesicon.svg";
import buttonarrow from "../../assets/images/buttonarrow.svg";
import { useTranslation } from "react-i18next";
import Checkbox from "antd/es/checkbox/Checkbox";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { guestUserLogout } from "../../common/guestUserLogout";
import noImageFound from "../../assets/images/noImageFound.svg";
import noVideoFound from "../../assets/images/noVideoFound.svg";
import nodataclaim from "../../assets/images/nodataclaim.svg";

import Loader from "../Loder/loader";
import { sanitizeSignatureInput } from "../../common/CommonFunctions";
// Since we don't have the exact image/video assets, we'll use empty containers that match the style
const MinusOutlined = () => (
  <svg
    id="Group_1174"
    data-name="Group 1174"
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
  >
    <defs>
      <clipPath id="clip-path">
        <rect
          id="Rectangle_439"
          data-name="Rectangle 439"
          width="24"
          height="24"
          fill="#15345a"
        />
      </clipPath>
    </defs>
    <rect
      id="Rectangle_441"
      data-name="Rectangle 441"
      width="24"
      height="24"
      fill="none"
    />
    <g id="Group_1174-2" data-name="Group 1174" transform="translate(0 0.48)">
      <rect
        id="Rectangle_440"
        data-name="Rectangle 440"
        width="18.24"
        height="19.2"
        transform="translate(3.84 1.92)"
        fill="none"
      />
      <g id="fi-rs-add" clip-path="url(#clip-path)">
        <path
          id="Path_362"
          data-name="Path 362"
          d="M9.14,0A9.15,9.15,0,1,0,15.6,2.68,9.14,9.14,0,0,0,9.14,0Zm0,16.756a7.625,7.625,0,1,1,5.383-2.233A7.616,7.616,0,0,1,9.14,16.756ZM9.9,8.378h3.047V9.9H5.331V8.378H9.9Z"
          transform="translate(2.381 2.38)"
          fill="#15345a"
        />
      </g>
    </g>
  </svg>
);
const PlusOutlined = () => (
  <svg
    id="Group_1175"
    data-name="Group 1175"
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
  >
    <defs>
      <clipPath id="clip-path">
        <rect
          id="Rectangle_442"
          data-name="Rectangle 442"
          width="20"
          height="20"
          fill="#15345a"
        />
      </clipPath>
    </defs>
    <rect
      id="Rectangle_443"
      data-name="Rectangle 443"
      width="24"
      height="24"
      fill="none"
    />
    <g id="fi-rs-add" transform="translate(2 2)" clip-path="url(#clip-path)">
      <path
        id="Path_363"
        data-name="Path 363"
        d="M10,0a10.011,10.011,0,1,0,7.068,2.932A10,10,0,0,0,10,0Zm0,18.333a8.342,8.342,0,1,1,5.89-2.443A8.333,8.333,0,0,1,10,18.333Zm.833-9.167h3.333v1.667H10.833v3.333H9.167V10.833H5.833V9.167H9.167V5.833h1.667Z"
        transform="translate(0)"
        fill="#15345a"
      />
    </g>
  </svg>
);
const { Panel } = Collapse;

export const EducationalMaterialEmpty = ({ titleKey, imageIcon }) => {
  const { t } = useTranslation();
  return (
    <div className="mobileMargin noData noMobileMargin">
      <div>
        <img src={imageIcon} alt="" />
        <div className="d-flex flex-column align-items-center justify-content-center mt-24 mb-4">
          <h3 className="fs-20 fw-700 colorblur">{t(titleKey)}</h3>
          <p className="fs-16">{t("There is no educational material found")}</p>
        </div>
      </div>
    </div>
  );
};

/** Runs when pdf.js reports a load error (Viewer has no onDocumentLoadFail prop). */
const PdfDocumentErrorBridge = ({ onFail }) => {
  useLayoutEffect(() => {
    onFail();
  }, [onFail]);
  return null;
};

export const EducationalImage = ({ imageUrl }) => {
  const [isLoading, setIsLoading] = useState(!!imageUrl);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    setFailed(false);
    setIsLoading(!!imageUrl);
  }, [imageUrl]);

  if (!imageUrl || failed) {
    return <EducationalMaterialEmpty titleKey="No image found" imageIcon={noImageFound} />;
  }

  return (
    <>
      {isLoading && <Loader />}
      <img
        src={imageUrl}
        className=" w-100 object-cover"
        alt=""
        onLoad={() => setIsLoading(false)}
        onError={() => {
          setIsLoading(false);
          setFailed(true);
        }}
      />
    </>
  );
};

export const EducationalVideo = ({ videoUrl }) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(!!videoUrl);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
    setIsLoading(!!videoUrl);
    setIsPlaying(false);
  }, [videoUrl]);

  const tryAutoplay = useCallback(() => {
    const el = videoRef.current;
    if (!el) return;
    el.play().catch(() => {
      /* Autoplay blocked or not allowed — user can tap play */
    });
  }, []);

  const handleLoadedData = useCallback(() => {
    setIsLoading(false);
    tryAutoplay();
  }, [tryAutoplay]);

  const handlePlayToggle = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play().catch(() => { });
      }
    }
  };

  if (!videoUrl || failed) {
    return <EducationalMaterialEmpty titleKey="No video found" imageIcon={noVideoFound} />;
  }

  return (
    <div
      className="video-container position-relative"
      style={{ overflow: "hidden" }}
    >
      {isLoading && <Loader />}
      <video
        ref={videoRef}
        className="w-100 h-100 object-cover cursor-pointer"
        playsInline
        onClick={handlePlayToggle}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => setIsPlaying(false)}
        onLoadedData={handleLoadedData}
        onError={() => {
          setIsLoading(false);
          setFailed(true);
        }}
        poster="https://media.istockphoto.com/id/1368940561/photo/shot-of-a-young-doctor-using-a-laptop-while-working-in-a-clinic.jpg?s=612x612&w=0&k=20&c=wXWd8Wp4rM4uU37jK_R1bZq711jQvQ9D7B7Kq1H0G_g="
      >
        <source src={videoUrl} type="video/mp4" />
        Your browser does not support HTML video.
      </video>
      {!isPlaying && (
        <div className="playbutton" onClick={handlePlayToggle}>
          <img src={playbutton} className="cursor-pointer" alt="" />
        </div>
      )}
    </div>
  );
};

export const EducationalPdfViewer = ({ pdfUrl }) => {
  const [isLoading, setIsLoading] = useState(!!pdfUrl);
  const [failed, setFailed] = useState(false);

  const onPdfLoadError = useCallback(() => {
    setIsLoading(false);
    setFailed(true);
  }, []);

  useEffect(() => {
    setFailed(false);
    setIsLoading(!!pdfUrl);
  }, [pdfUrl]);

  if (!pdfUrl || failed) {
    return <EducationalMaterialEmpty titleKey="No document found" imageIcon={nodataclaim} />;
  }

  return (
    <>
      {isLoading && <Loader />}
      <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
        <Viewer
          fileUrl={pdfUrl}
          onDocumentLoad={() => setIsLoading(false)}
          renderError={() => <PdfDocumentErrorBridge onFail={onPdfLoadError} />}
        />
      </Worker>
    </>
  );
};

export const EducationalFAQs = ({ faqs = [] }) => {
  const customExpandIcon = ({ isActive }) =>
    isActive ? <MinusOutlined /> : <PlusOutlined />;

  const items = (faqs || [])
    .filter((x) => x?.question || x?.answer)
    .sort((a, b) => (a?.displayOrder ?? 0) - (b?.displayOrder ?? 0));

  return (
    <div className="faq-container">
      <Collapse
        accordion
        expandIconPosition="end"
        defaultActiveKey={items?.length ? ["0"] : undefined}
        expandIcon={customExpandIcon}
      >
        {items.length ? (
          items.map((item, idx) => (
            <Panel header={item?.question || ""} key={String(idx)}>
              {item?.answer || ""}
            </Panel>
          ))
        ) : (
          <Panel header="No FAQs available" key="empty">
            Please check back later.
          </Panel>
        )}
      </Collapse>
    </div>
  );
};

const formatPaycheckUsd = (value) => {
  if (value == null) return "—";
  const normalized =
    typeof value === "string" ? value.replace(/[^0-9.-]/g, "") : value;
  const amount = Number(normalized);
  if (Number.isNaN(amount)) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

const PaycheckUpdatedInfoIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 18 18"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
    className="paycheck-updated-callout__icon"
  >
    <path
      d="M9 16.5C4.8675 16.5 1.5 13.1325 1.5 9C1.5 4.8675 4.8675 1.5 9 1.5C13.1325 1.5 16.5 4.8675 16.5 9C16.5 13.1325 13.1325 16.5 9 16.5ZM8.4375 12C8.4375 12.3075 8.6925 12.5625 9 12.5625C9.3075 12.5625 9.5625 12.3075 9.5625 12V8.25C9.5625 7.9425 9.3075 7.6875 9 7.6875C8.6925 7.6875 8.4375 7.9425 8.4375 8.25V12ZM9.69 5.715C9.6525 5.6175 9.6 5.5425 9.5325 5.4675C9.4575 5.4 9.375 5.3475 9.285 5.31C9.195 5.2725 9.0975 5.25 9 5.25C8.9025 5.25 8.805 5.2725 8.715 5.31C8.625 5.3475 8.5425 5.4 8.4675 5.4675C8.4 5.5425 8.3475 5.6175 8.31 5.715C8.2725 5.805 8.25 5.9025 8.25 6C8.25 6.0975 8.2725 6.195 8.31 6.285C8.3475 6.375 8.4 6.4575 8.4675 6.5325C8.5425 6.6 8.625 6.6525 8.715 6.69C8.895 6.765 9.105 6.765 9.285 6.69C9.375 6.6525 9.4575 6.6 9.5325 6.5325C9.6 6.4575 9.6525 6.375 9.69 6.285C9.7275 6.195 9.75 6.0975 9.75 6C9.75 5.9025 9.7275 5.805 9.69 5.715Z"
      fill="#15345a"
    />
  </svg>
);

export const EducationalPaycheck = ({ illustration, showPaycheckUpdatedCallout }) => {
  const { t } = useTranslation();
  const d = illustration && typeof illustration === "object" ? illustration : {};

  const premiumWith =
    d.primecarePremium != null
      ? formatPaycheckUsd(d.primecarePremium)
      : "-";

  const afterTax =
    d.afterTaxClaimPayment != null
      ? `${formatPaycheckUsd(d.afterTaxClaimPayment)}`
      : "—";

  const taxSavingsRaw = d.taxSavingsEstimate;
  // const taxSavingsDisplay =
  //   taxSavingsRaw != null && !Number.isNaN(Number(taxSavingsRaw))
  //     ? `${formatPaycheckUsd(Math.abs(Number(taxSavingsRaw)))}`
  //     : "—";

  return (
    <div className="paycheck-container ">
      {showPaycheckUpdatedCallout && (
        <div
          className="bordercard paycheck-updated-callout mb-40"
          role="status"
        >
          <div
            className="d-flex gap-8 mb-8"
            style={{ alignItems: "flex-start" }}
          >
            <PaycheckUpdatedInfoIcon />
            <p className="fs-16 fw-600 blueColor m-0">
              {t("Paycheck Updated")}
            </p>
          </div>
          <p className="fs-14 colorblur m-0 paycheck-updated-callout__body">
            {t(
              "Your paycheck has been updated since your last visit. Please review the changes",
            )}
          </p>
        </div>
      )}
      <div className="mb-40 bordercard">
        <div className=" p-20">
          <div className="d-flex justify-between align-center belowline">
            <div>
              <div className="fs-14 colorblur mb-4">
                {t("Current Paycheck")}
              </div>
              <div className="fs-18 fw-600 pColor">
                {formatPaycheckUsd(d.currentPaycheck)}
              </div>
            </div>
            <div className="paycheck-badge fs-12">{t("Without Benefits")}</div>
          </div>
          <div className="d-flex justify-between align-center px-20 pb-20 pt-16">
            <div>
              <div className="fs-14 colorblur mb-6">
                {t("After Enrollment")}
              </div>
              <div className="fs-18 fw-600 greencolor">
                {formatPaycheckUsd(d.afterEnrollmentWithBenefits)}
              </div>
            </div>
            <div className="paycheck-badge fs-12">{t("With Benefits")}</div>
          </div>
        </div>
        <div className="p-12 fs-14 text-center colorblur bg-gray">
          {t("You save")}{" "}
          <span className="fw-600 greencolor fs-14">
            {formatPaycheckUsd(d.yearlySavings)} {t("per year")}
          </span>{" "}
          {t("with PrimeCare")}
        </div>
      </div>
      <div className="mb-40 bordercard">
        <div className="p-20 blueColor fs-16 fw-600">
          Paycheck Breakdown
        </div>
        <div className="divider24 m-0"></div>
        <div className="p-20">
          <table className=" table">
            <thead>
              <tr>
                <th className="fs-14 fw-600 blueColor" style={{ textAlign: "left" }}>
                  {t("Line Item")}
                </th>
                <th className="fs-14 fw-600 blueColor" style={{ textAlign: "right" }}>
                  {t("Without PCGS")}
                </th>
                <th className="fs-14 fw-600 blueColor" style={{ textAlign: "right" }}>
                  {t("With PCGS")}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="fs-14 colorblur">
                  {t("Gross Pay")}
                </td>
                <td className="fs-14 colorblur" style={{ textAlign: "right" }}>
                  {formatPaycheckUsd(d.grossPayWithoutBenefits)}
                </td>
                <td className="fs-14 colorblur" style={{ textAlign: "right" }}>
                  {formatPaycheckUsd(d.grossPayWithBenefits)}
                </td>
              </tr>
              <tr>
                <td className="fs-14 colorblur">
                  {t("Federal Income Tax")}
                </td>
                <td className="fs-14 colorblur" style={{ textAlign: "right" }}>
                  {formatPaycheckUsd(d.federalIncomeTaxWithoutBenefits)}
                </td>
                <td className="fs-14 colorblur" style={{ textAlign: "right" }}>
                  {formatPaycheckUsd(d.federalIncomeTaxWithBenefits)}
                </td>
              </tr>
              <tr>
                <td className="fs-14 colorblur">
                  {t("FICA")}
                </td>
                <td className="fs-14 colorblur" style={{ textAlign: "right" }}>
                  {formatPaycheckUsd(d.ficaWithoutBenefits)}
                </td>
                <td className="fs-14 colorblur" style={{ textAlign: "right" }}>
                  {formatPaycheckUsd(d.ficaWithBenefits)}
                </td>
              </tr>
              <tr>
                <td className="fs-14 colorblur">
                  {t("Medicare")}
                </td>
                <td className="fs-14 colorblur" style={{ textAlign: "right" }}>
                  {formatPaycheckUsd(d.medicareWithoutBenefits)}
                </td>
                <td className="fs-14 colorblur" style={{ textAlign: "right" }}>
                  {formatPaycheckUsd(d.medicareWithBenefits)}
                </td>
              </tr>
              <tr>
                <td className="fs-14 colorblur">
                  {t("State Income Tax")}
                </td>
                <td className="fs-14 colorblur" style={{ textAlign: "right" }}>
                  {formatPaycheckUsd(d.stateIncomeTaxWithoutBenefits)}
                </td>
                <td className="fs-14 colorblur" style={{ textAlign: "right" }}>
                  {formatPaycheckUsd(d.stateIncomeTaxWithBenefits)}
                </td>
              </tr>
              <tr>
                <td className="fs-14 colorblur">
                  {t("PrimeCare Premium")}
                </td>
                <td className="fs-14 colorblur" style={{ textAlign: "right" }}>
                  -
                </td>
                <td className="fs-14 colorblur" style={{ textAlign: "right" }}>
                  {premiumWith}
                </td>
              </tr>
              <tr>
                <td className="fs-14 colorblur">
                  {t("After-Tax Claim Payment")}
                </td>
                <td className="fs-14 colorblur" style={{ textAlign: "right" }}>
                  -
                </td>
                <td className="fs-14 colorblur" style={{ textAlign: "right" }}>
                  {afterTax}
                </td>
              </tr>

              <tr>
                <td colSpan="3" ><div className="divider24 m-0"></div></td>
              </tr>

              <tr>
                <td className="fs-14 fw-700">
                  {t("Net Pay")}
                </td>
                <td className="fs-14 fw-700 blueColor" style={{ textAlign: "right" }}>
                  {formatPaycheckUsd(d.netPayWithoutBenefits)}
                </td>
                <td className="fs-14 fw-700 greencolor" style={{ textAlign: "right" }}>
                  {formatPaycheckUsd(d.netPayWithBenefits)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div className="mb-50">
        <div className="bordercard p-20 d-flex gap-20 align-center">
          <div className="icon-wrapper">
            <img src={paycheck1} alt="" />
          </div>
          <div>
            <div className="fs-14 colorblur mb-8">{t("Monthly Total Savings with PCGS")}</div>
            <div className="fs-18 fw-600 greencolor">
              {d.netBenefitPerMonth != null
                ? formatPaycheckUsd(d.netBenefitPerMonth)
                : "—"}
            </div>
          </div>
        </div>
      </div>
      {/* <div className=" mb-50 grid-2 gap-20">
        <div className="bordercard p-20">
          <div className="icon-wrapper mb-20">
            <img src={paycheck1} alt="" />
          </div>
          <div>
            <div className="fs-14 colorblur mb-8">{t("Monthly Premium")}</div>
            <div className="fs-18 fw-600 pColor">
              {d.monthlyPremium != null
                ? formatPaycheckUsd(d.monthlyPremium)
                : "—"}
            </div>
          </div>
        </div>
        <div className="bordercard p-20">
          <div className="icon-wrapper mb-20">
            <img src={paycheck2} alt="" />
          </div>
          <div>
            <div className="fs-14 colorblur mb-8">
              {t("Tax Savings (Est.)")}
            </div>
            <div className="fs-18 fw-600 greencolor">{taxSavingsDisplay}</div>
          </div>
        </div>
        <div className="bordercard p-20">
          <div className="icon-wrapper mb-20">
            <img src={paycheck3} alt="" />
          </div>
          <div>
            <div className="fs-14 colorblur mb-8">{t("Claim payment")}</div>
            <div className="fs-18 fw-600 orangecolor">
              {formatPaycheckUsd(d.claimPayment)}
            </div>
          </div>
        </div>
        <div className="bordercard p-20">
          <div className="icon-wrapper mb-20">
            <img src={paycheck4} alt="" />
          </div>
          <div>
            <div className="fs-14 colorblur mb-8">{t("Net benefit / mo")}</div>
            <div className="fs-18 fw-600 purplecolor">
              {formatPaycheckUsd(d.netBenefitPerMonth)}
            </div>
          </div>
        </div>
      </div> */}
    </div>
  );
};

const CheckCircleFilled = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="10" cy="10" r="10" fill="#00A63E" />
    <path
      d="M14 7L8.5 12.5L6 10"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CloseCircleFilled = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="10" cy="10" r="10" fill="#E80000" />
    <path
      d="M13 7L7 13M7 7L13 13"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const EducationalEnrollment = ({ selected, setSelected }) => {
  const { t } = useTranslation();
  return (
    <div className="py-20 text-start">
      <div className="tgb fs-20 mb-12 blueColor">
        {t("Ready to Get Started?")}
      </div>
      <p className="fs-16 colorblur">
        {t("You've reviewed all the information.")}
        <br />
        {t("Please make your enrollment decision below.")}
      </p>

      {/* <div
        className={`enrollment-card mb-16 ${selected === "YES" ? "selected-yes" : ""}`}
        onClick={() => setSelected("YES")}
      >
        <div className="d-flex align-center gap-12">
          <CheckCircleFilled />
          <span className="fs-16 fw-600 colorblur">
            {t("Yes, I would like to keep these benefits")}
          </span>
        </div>
      </div>

      <div
        className={`enrollment-card mb-30 ${selected === "NO" ? "selected-no" : ""}`}
        onClick={() => setSelected("NO")}
      >
        <div className="d-flex align-center gap-12">
          <CloseCircleFilled />
          <span className="fs-16 fw-600 colorblur">
            {t("No, I do not want to keep these benefits")}
          </span>
        </div>
      </div> */}
      <div className="divider24"></div>
      <div className="help-box mb-20">
        <div className="fs-16 fw-700 colorblur mb-8">
          {t("Need help deciding?")}
        </div>
        <div className="fs-14 colorblur fw-400">
          {t("Contact our Account Management Team at")}{" "}
          <span className="pColor fw-500">(833) 373-0718</span> {t("or email")}{" "}
          <span className="pColor fw-500">myserviceteam@primecaregs.com</span>
        </div>
      </div>
    </div>
  );
};

export const EducationalThankYouYes = ({ onChangeDecision }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const onChangeDecisionYES = () => {
    guestUserLogout(dispatch, navigate);
  };

  return (
    <div className="py-20 w-100">
      <div className="paycheck-block mb-50 text-center mx-auto">
        <div className="mb-30 d-flex justify-center">
          <img src={yesicon} alt="" />
        </div>
        <div className="fs-20 fw-700 blueColor mb-8">
          {t("Enrollment Complete!")}
        </div>
        <div className="fs-16 blueColor mb-30">
          {t("Welcome to PCGS Benefits")}
        </div>
        <div className="divider24" />
        <div className="fs-16 lh-20 blueColor">
          {t(
            "Congratulations! Your enrollment has been successfully submitted. You will receive a confirmation email within 24 hours with your policy details and member ID.",
          )}
        </div>
      </div>

      <div className="paycheck-block mb-50 p20 text-start mx-auto">
        <div className="fs-20 fw-700 blueColor mb-16">
          {t("What happen next")}
        </div>
        <ul className="benefits-list m-0 fs-16 colorblur">
          <li>{t("Check your email for confirmation and policy documents")}</li>
          <li>
            {t("Your coverage will begin on the date specified in your policy")}
          </li>
          <li>
            {t("Download the PCGS mobile app for easy access to your benefits")}
          </li>
          <li>{t("Set up your online account at portal.pcgs.com")}</li>
        </ul>
      </div>

      <div className="mx-auto p-0">
        <button className="primaryButton w-100 mb-30 cursor-pointer fs-18" onClick={onChangeDecisionYES}>
          {t("Continue to Login")}
        </button>
        <div
          className="text-center cursor-pointer fs-18 fw-600 blueColor"
          onClick={onChangeDecision}
        >
          {t("Change Decision")}
        </div>
      </div>
    </div>
  );
};

export const EducationalThankYouNo = ({ onChangeDecision }) => {
  const { t } = useTranslation();
  return (
    <div className="py-20 w-100">
      <div className="paycheck-block mb-50 text-center mx-auto">
        <div className="mb-30 d-flex justify-center">
          <img src={noicon} alt="" />
        </div>
        <div className="fs-20 fw-700 blueColor mb-8">
          {t("Enrollment Declined")}
        </div>
        <div className="fs-16 blueColor mb-30">
          {t("We're sorry to see you go")}
        </div>
        <div className="divider24" />
        <div className="fs-16 lh-20 blueColor">
          {t(
            "Your decision has been recorded. If you change your mind during the next open enrollment period or experience a qualifying life event, you can enroll at that time.",
          )}
        </div>
      </div>

      <div className=" mx-auto p-0">
        <button
          className="primaryButton w-100 cursor-pointer fs-18"
          onClick={onChangeDecision}
        >
          {t("Change Decision")}
        </button>
      </div>
    </div>
  );
};


export const EducationalESign = ({
  signature,
  setSignature,
  agreed,
  setAgreed,
  htmlContent,
  documentUrl,
  signatureError,
  onSignatureBlur,
  onSignatureChange,
  onViewAgreement,
}) => {
  const { t } = useTranslation();
  const HtmlRenderer = ({ htmlContent }) => {
    const containerRef = useRef(null);

    useEffect(() => {
      if (!containerRef.current) return;

      const shadowRoot = containerRef.current.attachShadow({ mode: "open" });
      shadowRoot.innerHTML = htmlContent;
    }, [htmlContent]);

    return <div className="fs-16 colorblur mb-16 lh-20  " ref={containerRef} />;
  };

  return (
    <div className="py-20 text-start w-100">
      <div className="mb-40">
        <div className="fs-20 fw-700 blueColor mb-12 tgb">
          {t("Sign your agreement")}
        </div>
        <p className="fs-16 colorblur">
          {t(
            "Required to collect information from your provider and support your healthcare needs.",
          )}
        </p>
      </div>

      <div className="enrollmentSectionSign mb-16">
        <div className="esignScrolling">

          <div className="mb-40 ">
            <div className=" fs-20 fw-700 blueColor mb-20 tgb ">{t("Agreement")}</div>
            {htmlContent && (
              // <div
              //   className="fs-16 colorblur mb-16 lh-20"
              //   dangerouslySetInnerHTML={{ __html: htmlContent }}
              // />
              <HtmlRenderer htmlContent={htmlContent} />
            )}
          </div>
          <button
            type="button"
            className="primaryButton outlinebtn outlinebtnblue w-100 mb-40 gap-8"
            onClick={() => {
              if (onViewAgreement) {
                onViewAgreement();
                return;
              }
              if (documentUrl) {
                window.open(documentUrl, "_blank", "noopener,noreferrer");
              }
            }}
            disabled={!documentUrl}
          >
            {t("View Enrollment Agreement")} <img src={buttonarrow} alt="" />
          </button>
        </div>
      </div>

      <div className="mb-20">
        <div className="fs-16 fw-700 blueColor mb-12">
          {t("Type your full name to sign")}
        </div>
        <input
          type="text"
          className=" inputField mb-24"
          placeholder="Please Enter Your Name"
          value={signature}
          style={{ color: "black" }}
          onChange={(e) => {
            const nextValue = sanitizeSignatureInput(e.target.value);
            if (onSignatureChange) {
              onSignatureChange(nextValue);
              return;
            }
            setSignature(nextValue);
          }}
          onBlur={() => {
            if (onSignatureBlur) {
              onSignatureBlur();
            }
          }}
        />
        {!!signatureError && (
          <p className="fs-16 mb-16" style={{ color: "#E80000" }}>
            {signatureError}
          </p>
        )}

        <div className="d-flex align-start gap-12 ">
          <Checkbox
            className="customCheckbox gap4 fs-16 lh-20 p-0"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
          >
            {t(
              "I have read and agree to the Terms & Conditions. My typed name is my legal electronic signature.",
            )}
          </Checkbox>
        </div>
      </div>
    </div>
  );
};
